/**
 * "Ranked on" — a ranking formula editor (hours, and texts): one collapsible
 * card per date range (the Flow Optimizer's sliders or blocks over its whole
 * metric catalog), shares across ranges, the warning when shares leave 100,
 * and the formula in words. Each metric can rank by its lowest value
 * (02/11/2026 — "↑ Highest / ↓ Lowest", lowest(metric) in the blocks).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Plus, Sigma } from 'lucide-react';
import {
  DROP_GEN_HOUR_MAX_GROUPS,
  type DropGenHourFormula,
  describeHourFormula,
  type FlowOptimizerMetricMeta,
  normalizedHourGroupWeights,
} from '@trafficpulse/shared';

import { Button, HintDot } from '@/components/ui';
import { FormulaMetricCatalogProvider } from '@/features/flow-optimizer/tabs/calculation/metric-catalog/FormulaMetricCatalogContext';
import { LowestProvider } from '@/features/flow-optimizer/tabs/calculation/sliders/lowest';
import WeightTotalsWarning from '@/features/flow-optimizer/tabs/calculation/WeightTotalsWarning';

import { FIELD_ICON, FIELD_LABEL } from '../../fields/field-chrome';
import { addHourGroup, removeHourGroup, replaceHourGroup, setHourGroupWeight } from './hour-formula.model';
import { HOUR_METRIC_CATALOG } from './hour-metric-catalog';
import { HourFormulaGroupCard } from './HourFormulaGroupCard';

interface Props {
  formula: DropGenHourFormula;
  onChange: (next: DropGenHourFormula) => void;
  /** What the formula ranks (hours by default; the text ranking passes its own). */
  hint?: string;
  /** Inside a labelled row: no "Ranked on" line of its own. */
  bare?: boolean;
  /** Route tests: every range stays today and the picker does not open. */
  dateLocked?: boolean;
  /** The picker's metrics; default the Flow catalog (a route test on its drops adds SMSWiser's). */
  metrics?: FlowOptimizerMetricMeta[];
}

const HINT =
  "Like the Flow Optimizer: each date range is a group with its own formula — sliders or blocks — over the Flow Optimizer's whole metric catalog; with several ranges their shares sum to 100%. Rates (CR, ROI, EPV…) compare fairly; counts (visits, conversions, revenue) mostly re-elect the hours already used most. The “Not enough data” rule reads the first range.";

export const HourFormulaCard: FC<Props> = ({ formula, onChange, hint = HINT, bare = false, dateLocked = false, metrics = HOUR_METRIC_CATALOG }) => {
  const shares = normalizedHourGroupWeights(formula);
  const total = formula.groups.reduce((s, g) => s + (formula.groupWeights[g.id] ?? 0), 0);
  const issues =
    formula.groups.length > 1 && total > 0 && Math.round(total) !== 100
      ? [{ label: 'Range shares', total: Math.round(total) }]
      : [];
  return (
    <FormulaMetricCatalogProvider metrics={metrics}>
      <LowestProvider>
        <div className="flex flex-col gap-2" data-testid="hour-formula-card">
          {bare ? null : (
            <div className="flex flex-wrap items-center gap-2">
              <span className={FIELD_LABEL}>
                <Sigma className={FIELD_ICON} /> Ranked on
                <HintDot label={hint} />
              </span>
              <span className="text-xs text-muted-foreground">{describeHourFormula(formula)}</span>
            </div>
          )}
          {formula.groups.map((group, index) => (
            <HourFormulaGroupCard
              key={group.id}
              group={group}
              index={index}
              count={formula.groups.length}
              share={shares[group.id] ?? 0}
              onChange={(next) => onChange(replaceHourGroup(formula, next))}
              onShare={(share) => onChange(setHourGroupWeight(formula, group.id, share))}
              onRemove={() => onChange(removeHourGroup(formula, group.id))}
              dateLocked={dateLocked}
            />
          ))}
          <WeightTotalsWarning issues={issues} />
          {formula.groups.length < DROP_GEN_HOUR_MAX_GROUPS ? (
            <Button variant="outline" size="sm" className="self-start" onClick={() => onChange(addHourGroup(formula))}>
              <Plus className="mr-1 h-3.5 w-3.5" /> Add a date range
            </Button>
          ) : null}
        </div>
      </LowestProvider>
    </FormulaMetricCatalogProvider>
  );
};
